import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getCountryFlag, getCountryName } from '../utils/countries';
import './AdminAnalytics.css';
import { localeFor } from '../utils/timeFormat';

// Аналитика для админа: новые пользователи, входы на сайт и новые интеграции
// ROUVY по суткам. Что именно считается и где история неполная — см. AdminAnalyticsService
const PERIODS = [7, 30, 90];
// Весь период — с первой регистрации на проекте
const ALL_TIME = 'all';
const DEFAULT_PERIOD = 30;

// Сутки режем в часовом поясе браузера: так последний столбик совпадает
// с «сегодня» того, кто смотрит. Неизвестный пояс сервер заменит на UTC
const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (error) {
    return 'UTC';
  }
};

// Дата приходит как YYYY-MM-DD уже в нужном поясе — разбираем как локальную,
// иначе new Date('2026-09-14') даст полночь UTC и в западных поясах сдвинет день
const parseDate = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Имя как в рейтинге и протоколах: латиницей для английского, если она заполнена
const userName = (user, language) => {
  const name = language !== 'ru' && user.first_name_eng && user.last_name_eng
    ? `${user.first_name_eng} ${user.last_name_eng}`
    : [user.first_name, user.last_name].filter(Boolean).join(' ');

  return name || user.nickname;
};

// Числа и даты в формате текущего языка интерфейса
const useFormatters = () => {
  const { language } = useLanguage();
  const locale = localeFor(language, 'en-GB');
  const numberFormat = new Intl.NumberFormat(locale);

  return {
    number: value => numberFormat.format(value),
    axisDate: iso => parseDate(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
    date: iso => parseDate(iso).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long' }),
    fullDate: iso => parseDate(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
  };
};

// Шкала только по целым: счетчики небольшие, деление «2,5 входа» бессмысленно
const integerScale = (maxValue, tickCount = 4) => {
  const safeMax = Math.max(maxValue, 1);
  const raw = safeMax / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = Math.max(1, [1, 2, 5, 10].map(multiplier => multiplier * magnitude).find(value => value >= raw));
  const top = Math.ceil(safeMax / step) * step;
  const ticks = [];
  for (let value = 0; value <= top; value += step) ticks.push(value);
  return { top, ticks };
};

// Столбик растет от нулевой линии: верх скруглен, основание прямое
const columnPath = (x, top, width, bottom) => {
  const radius = Math.min(4, width / 2, bottom - top);
  return [
    `M${x},${bottom}`,
    `V${top + radius}`,
    `Q${x},${top} ${x + radius},${top}`,
    `H${x + width - radius}`,
    `Q${x + width},${top} ${x + width},${top + radius}`,
    `V${bottom}`,
    'Z'
  ].join(' ');
};

// onSelect — выбор дня щелчком по столбику (или Enter с клавиатуры);
// без него график только показывает подсказки
const DailyColumnChart = ({ days, valueKey, tooltipRows, ariaLabel, selectedIndex = null, onSelect }) => {
  const { t } = useLanguage();
  const format = useFormatters();
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(720);
  const [active, setActive] = useState(null);

  // Рисуем в пикселях контейнера, а не растягиваем viewBox: иначе на телефоне
  // подписи осей сжимаются вместе с графиком до нечитаемых
  useLayoutEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;

    const measure = () => setWidth(Math.max(200, Math.round(node.clientWidth)));
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setActive(null);
  }, [days.length]);

  const compact = width < 520;
  const height = compact ? 190 : 230;
  const margin = { top: 12, right: 8, bottom: 28, left: 40 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const baseline = margin.top + plotHeight;

  const { top, ticks } = integerScale(Math.max(0, ...days.map(day => day[valueKey])));
  const band = plotWidth / days.length;
  const barWidth = Math.max(2, Math.min(24, band * 0.64));
  const y = value => baseline - (value / top) * plotHeight;
  const bandCenter = index => margin.left + index * band + band / 2;

  // Не больше одной подписи даты на ~72px; отсчитываем от конца, чтобы
  // сегодняшний день был подписан всегда
  const labelEvery = Math.max(1, Math.ceil(72 / band));
  const lastIndex = days.length - 1;

  const handleKeyDown = (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && onSelect && active !== null) {
      event.preventDefault();
      onSelect(active);
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const step = event.key === 'ArrowLeft' ? -1 : 1;
    setActive(current => Math.min(lastIndex, Math.max(0, current === null ? lastIndex : current + step)));
  };

  const activeDay = active !== null ? days[active] : null;

  // Подсказка встает сбоку от столбика, чтобы не закрывать его вершину: в левой
  // половине графика — справа, в правой — слева. На узком экране места сбоку
  // нет, там она уходит под график
  let tooltipStyle;
  if (active !== null && !compact) {
    const offset = band / 2 + 8;
    const center = bandCenter(active);
    tooltipStyle = center < width / 2 ? { left: center + offset } : { right: width - center + offset };
  }

  return (
    <div className="admin-analytics-chart" ref={wrapRef}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={() => setActive(current => (current === null ? lastIndex : current))}
        onBlur={() => setActive(null)}
        onMouseLeave={() => setActive(null)}
      >
        {ticks.map(tick => (
          <g key={tick}>
            <line
              x1={margin.left}
              x2={margin.left + plotWidth}
              y1={Math.round(y(tick)) + 0.5}
              y2={Math.round(y(tick)) + 0.5}
              className="admin-analytics-grid"
            />
            <text x={margin.left - 8} y={y(tick) + 4} textAnchor="end" className="admin-analytics-axis">
              {format.number(tick)}
            </text>
          </g>
        ))}

        {days.map((day, index) => (
          <rect
            key={`hit-${day.date}`}
            x={margin.left + index * band}
            y={margin.top}
            width={band}
            height={plotHeight}
            className={[
              'admin-analytics-hit',
              onSelect ? 'admin-analytics-hit--selectable' : '',
              index === active ? 'admin-analytics-hit--active' : '',
              index === selectedIndex ? 'admin-analytics-hit--selected' : ''
            ].filter(Boolean).join(' ')}
            onMouseEnter={() => setActive(index)}
            onTouchStart={() => setActive(index)}
            onClick={onSelect ? () => onSelect(index) : undefined}
          />
        ))}

        {days.map((day, index) => {
          const value = day[valueKey];
          if (value <= 0) return null;

          return (
            <path
              key={`column-${day.date}`}
              d={columnPath(bandCenter(index) - barWidth / 2, y(value), barWidth, baseline)}
              className={`admin-analytics-column ${index === lastIndex ? 'admin-analytics-column--partial' : ''}`}
            />
          );
        })}

        {days.map((day, index) => {
          if ((lastIndex - index) % labelEvery !== 0) return null;

          const center = bandCenter(index);
          let anchor = 'middle';
          if (center > width - 32) anchor = 'end';
          else if (center < margin.left + 16) anchor = 'start';

          return (
            <text
              key={`label-${day.date}`}
              x={anchor === 'end' ? width - margin.right : center}
              y={height - 8}
              textAnchor={anchor}
              className="admin-analytics-axis"
            >
              {format.axisDate(day.date)}
            </text>
          );
        })}
      </svg>

      {activeDay && (
        <div
          className={`admin-analytics-tooltip ${compact ? 'admin-analytics-tooltip--below' : ''}`}
          style={tooltipStyle}
        >
          <div className="admin-analytics-tooltip-date">
            {format.date(activeDay.date)}
            {active === lastIndex && <span>{t('admin.analytics.partialDay')}</span>}
          </div>
          {tooltipRows(activeDay).map(row => (
            <div key={row.label} className="admin-analytics-tooltip-row">
              <span>{row.label}</span>
              <strong>{format.number(row.value)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Тексты списков под графиками: кто зарегистрировался и кто подключил ROUVY
const DAY_USERS_LISTS = {
  newUsers: {
    endpoint: '/api/admin/analytics/new_users',
    titleKey: 'admin.analytics.newUsersOn',
    emptyDayKey: 'admin.analytics.noNewUsersDay',
    emptyPeriodKey: 'admin.analytics.noNewUsersPeriod'
  },
  rouvyUsers: {
    endpoint: '/api/admin/analytics/rouvy_users',
    titleKey: 'admin.analytics.rouvyUsersOn',
    emptyDayKey: 'admin.analytics.noRouvyUsersDay',
    emptyPeriodKey: 'admin.analytics.noRouvyUsersPeriod'
  }
};

// Пользователи за выбранные сутки: флаг страны и ФИО со ссылкой на профиль
const DayUsersList = ({ list, date, count }) => {
  const { t, language } = useLanguage();
  const format = useFormatters();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const { endpoint, titleKey, emptyDayKey, emptyPeriodKey } = DAY_USERS_LISTS[list];

  useEffect(() => {
    // Счетчик за сутки уже известен из графика: при нуле запрос не нужен
    if (!date || count === 0) {
      setUsers([]);
      setLoading(false);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    axios
      .get(endpoint, { params: { date, time_zone: browserTimeZone() } })
      .then(response => {
        if (cancelled) return;
        setUsers(response.data.users || []);
        setFailed(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error(`Failed to fetch ${list} users:`, err);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint, list, date, count]);

  if (!date) {
    return <p className="admin-analytics-users-empty">{t(emptyPeriodKey)}</p>;
  }

  let content;
  if (count === 0) {
    content = <p className="admin-analytics-users-empty">{t(emptyDayKey)}</p>;
  } else if (failed) {
    content = <p className="admin-analytics-users-error">{t('admin.analytics.usersLoadError')}</p>;
  } else if (loading && users.length === 0) {
    content = <p className="admin-analytics-users-empty">{t('common.loading')}</p>;
  } else {
    content = (
      <ul className={`admin-analytics-users-list ${loading ? 'is-refreshing' : ''}`}>
        {users.map(user => {
          const flag = user.country ? getCountryFlag(user.country) : null;

          return (
            <li key={user.id}>
              <span
                className="admin-analytics-users-flag"
                role={flag ? 'img' : undefined}
                aria-label={flag ? getCountryName(user.country, language) : undefined}
                title={flag ? getCountryName(user.country, language) : undefined}
              >
                {flag}
              </span>
              <Link to={`/riders/${user.id}`} title={t('results.open_rider')}>
                {userName(user, language)}
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="admin-analytics-users">
      <div className="admin-analytics-users-head">
        <span className="admin-analytics-users-title">
          {t(titleKey).replace('{date}', format.fullDate(date))}
          {count > 0 && <span className="admin-analytics-users-count">{format.number(count)}</span>}
        </span>
        <span className="admin-analytics-users-hint">{t('admin.analytics.selectDayHint')}</span>
      </div>
      {content}
    </div>
  );
};

const AnalyticsCard = ({ title, hint, stats, days, valueKey, tableColumns, tooltipRows, selectedIndex = null, onSelect, children }) => {
  const { t } = useLanguage();
  const format = useFormatters();
  const [showTable, setShowTable] = useState(false);
  const isEmpty = days.every(day => day[valueKey] === 0);

  // Строка таблицы выбирает день так же, как столбик: на длинном периоде
  // столбики на телефоне тоньше пальца
  const rowProps = (index) => {
    if (!onSelect) return {};

    return {
      className: `admin-analytics-row--selectable ${index === selectedIndex ? 'admin-analytics-row--selected' : ''}`,
      tabIndex: 0,
      onClick: () => onSelect(index),
      onKeyDown: (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onSelect(index);
      }
    };
  };

  return (
    <section className="admin-analytics-card">
      <h3>{title}</h3>
      <p className="admin-analytics-card-hint">{hint}</p>

      <div className="admin-analytics-stats">
        {stats.map(stat => (
          <div key={stat.label} className="admin-analytics-stat">
            <span className="admin-analytics-stat-label">{stat.label}</span>
            <span className="admin-analytics-stat-value">{format.number(stat.value)}</span>
          </div>
        ))}
      </div>

      <DailyColumnChart
        days={days}
        valueKey={valueKey}
        tooltipRows={tooltipRows}
        ariaLabel={title}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
      />

      <p className="admin-analytics-note">
        {isEmpty ? t('admin.analytics.empty') : t('admin.analytics.partialNote')}
      </p>

      {children}

      <button type="button" className="admin-analytics-table-toggle" onClick={() => setShowTable(value => !value)}>
        {showTable ? t('admin.analytics.hideTable') : t('admin.analytics.showTable')}
      </button>

      {showTable && (
        <div className="admin-analytics-table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('admin.analytics.date')}</th>
                {tableColumns.map(column => (
                  <th key={column.key} className="admin-analytics-num">{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((day, index) => ({ day, index })).reverse().map(({ day, index }) => (
                <tr key={day.date} {...rowProps(index)}>
                  <td>{format.date(day.date)}</td>
                  {tableColumns.map(column => (
                    <td key={column.key} className="admin-analytics-num">{format.number(day[column.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const AdminAnalytics = () => {
  const { t, pluralize } = useLanguage();
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Храним ключ перевода, а не текст: при смене языка сообщение переведется
  const [errorKey, setErrorKey] = useState(null);
  // Дни, за которые под графиками показаны списки новых пользователей
  // и подключивших ROUVY — у каждой карточки свой
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRouvyDate, setSelectedRouvyDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    axios
      .get('/api/admin/analytics', { params: { days: period, time_zone: browserTimeZone() } })
      .then(response => {
        if (cancelled) return;
        setData(response.data);
        setErrorKey(null);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to fetch analytics:', err);
        // Текст ошибки с сервера не показываем — он не переведен
        setErrorKey(err.response?.status === 403 ? 'admin.analytics.accessDenied' : 'admin.analytics.loadError');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // При быстром переключении периода ответ старого запроса не должен
    // перетереть свежий
    return () => {
      cancelled = true;
    };
  }, [period, reloadKey]);

  // По умолчанию — последний день периода с событиями: сегодня новых часто
  // нет, и пустой список ничего не говорит. Выбранный вручную день сохраняем,
  // если он попадает и в новый период
  useEffect(() => {
    if (!data) return;

    const keepOrLastWith = valueKey => (current) => {
      if (current && data.days.some(day => day.date === current)) return current;

      const lastWithEvents = [...data.days].reverse().find(day => day[valueKey] > 0);
      return lastWithEvents ? lastWithEvents.date : null;
    };

    setSelectedDate(keepOrLastWith('registrations'));
    setSelectedRouvyDate(keepOrLastWith('rouvy_connections'));
  }, [data]);

  const today = data?.days[data.days.length - 1];
  const selectedIndex = data ? data.days.findIndex(day => day.date === selectedDate) : -1;
  const selectedRouvyIndex = data ? data.days.findIndex(day => day.date === selectedRouvyDate) : -1;

  return (
    <div className="admin-analytics">
      <div className="admin-analytics-header">
        <h2>{t('admin.analytics.title')}</h2>
        {data && (
          <p className="admin-analytics-subtitle">
            {t('admin.analytics.subtitle').replace('{tz}', data.time_zone)}
          </p>
        )}
      </div>

      <div className="admin-analytics-periods" role="group" aria-label={t('admin.analytics.period')}>
        {[...PERIODS, ALL_TIME].map(days => (
          <button
            key={days}
            type="button"
            aria-pressed={period === days}
            onClick={() => setPeriod(days)}
          >
            {/* Склонение дней уже есть в переводах событий — как в EventsList */}
            {days === ALL_TIME
              ? t('admin.analytics.allTime')
              : `${days} ${pluralize(days, t('events.day'), t('events.days_few'), t('events.days_many'))}`}
          </button>
        ))}
      </div>

      {errorKey && (
        <div className="admin-analytics-error">
          {t(errorKey)}
          <button type="button" onClick={() => setReloadKey(key => key + 1)}>
            {t('admin.analytics.retry')}
          </button>
        </div>
      )}

      {!data && loading && <div className="admin-analytics-loading">{t('common.loading')}</div>}

      {data && (
        <div className={`admin-analytics-cards ${loading ? 'is-refreshing' : ''}`}>
          <AnalyticsCard
            title={t('admin.analytics.newUsersTitle')}
            hint={t('admin.analytics.newUsersHint')}
            stats={[
              { label: t('admin.analytics.registrationsTotal'), value: data.totals.registrations },
              { label: t('users.totalUsers'), value: data.totals.all_users },
              { label: t('admin.analytics.today'), value: today.registrations }
            ]}
            days={data.days}
            valueKey="registrations"
            tableColumns={[
              { key: 'registrations', label: t('admin.analytics.registrations') }
            ]}
            tooltipRows={day => [
              { label: t('admin.analytics.registrations'), value: day.registrations }
            ]}
            selectedIndex={selectedIndex >= 0 ? selectedIndex : null}
            onSelect={index => setSelectedDate(data.days[index].date)}
          >
            <DayUsersList
              list="newUsers"
              date={selectedDate}
              count={selectedIndex >= 0 ? data.days[selectedIndex].registrations : 0}
            />
          </AnalyticsCard>

          <AnalyticsCard
            title={t('admin.analytics.loginsTitle')}
            hint={t('admin.analytics.loginsHint')}
            stats={[
              { label: t('admin.analytics.loginsTotal'), value: data.totals.logins },
              { label: t('admin.analytics.uniqueUsersTotal'), value: data.totals.users },
              { label: t('admin.analytics.today'), value: today.logins }
            ]}
            days={data.days}
            valueKey="logins"
            tableColumns={[
              { key: 'logins', label: t('admin.analytics.logins') },
              { key: 'users', label: t('admin.analytics.uniqueUsers') }
            ]}
            tooltipRows={day => [
              { label: t('admin.analytics.logins'), value: day.logins },
              { label: t('admin.analytics.uniqueUsers'), value: day.users }
            ]}
          />

          <AnalyticsCard
            title={t('admin.analytics.rouvyTitle')}
            hint={t('admin.analytics.rouvyHint')}
            stats={[
              { label: t('admin.analytics.rouvyTotal'), value: data.totals.rouvy_connections },
              { label: t('admin.analytics.today'), value: today.rouvy_connections }
            ]}
            days={data.days}
            valueKey="rouvy_connections"
            tableColumns={[
              { key: 'rouvy_connections', label: t('admin.analytics.rouvyConnections') }
            ]}
            tooltipRows={day => [
              { label: t('admin.analytics.rouvyConnections'), value: day.rouvy_connections }
            ]}
            selectedIndex={selectedRouvyIndex >= 0 ? selectedRouvyIndex : null}
            onSelect={index => setSelectedRouvyDate(data.days[index].date)}
          >
            <DayUsersList
              list="rouvyUsers"
              date={selectedRouvyDate}
              count={selectedRouvyIndex >= 0 ? data.days[selectedRouvyIndex].rouvy_connections : 0}
            />
          </AnalyticsCard>
        </div>
      )}
    </div>
  );
};

// Раздел открыт только владельцу проекта (users.analytics_access), а не всем админам.
// Вход и деактивацию проверяет PrivateRoute; без флага уводим туда же, куда AdminRoute
const AdminAnalyticsPage = () => {
  const { user } = useAuth();

  if (!user?.analytics_access) {
    return <Navigate to="/profile" replace />;
  }

  return <AdminAnalytics />;
};

export default AdminAnalyticsPage;
